import { parseISO, format } from 'date-fns'
import type { ActivityType } from '@firstbite/ui'
import type { ActivityV2 } from '../../models/activityV2'
import type { EnrichedAction } from '../../services/recommendations_contracts'
import { SIGNAL_CONFIG } from './recommendationConfig'

/**
 * Which activity feed a rec's panel shows + the header label — the single source
 * of truth for both the query scope ({@link useRecommendationActivityQuery}) and
 * the "…Activity" header, so they can't drift. Deal-focused recs (deal-linked
 * signal, or an `auto` signal that has a deal) scope to the DEAL so the "Deal
 * Activity" header is accurate; everything else scopes to the company.
 */
export function resolveActivityScope(action: EnrichedAction): {
  dealId?: number
  chainProxyId?: number
  label: string
} {
  const linkTarget = SIGNAL_CONFIG[action.signal_type]?.linkTarget
  const useDeal = !!action.deal_id && linkTarget !== 'company'
  return useDeal
    ? { dealId: action.deal_id ?? undefined, label: 'Deal Activity' }
    : {
        chainProxyId: action.contact_company_id ?? undefined,
        label: 'Company Activity',
      }
}

/**
 * Users whose activity rows read as automation / AI rather than a person — the
 * same string sets EventIcon keys its lightning / sparkle glyphs off (they're
 * not derivable from content_type or action_type). Kept local so this isolated
 * homepage variant shares no module with the detail-page EventHistory stack.
 */
const AUTOMATION_USER_NAMES = new Set(['First Bite Automation'])
const AI_USER_NAMES = new Set([
  'First Bite AI',
  'AI Recommendation',
  'AI Recommendations',
])

/** content_type.model → fb-ui ActivityType (avatar glyph + tint). */
const MODEL_TYPE: Record<string, ActivityType> = {
  emailmessage: 'email',
  deal: 'deal',
  companynote: 'note',
  contactnote: 'note',
  doornote: 'note',
  dealnote: 'note',
  note: 'note',
  reminder: 'reminder',
  samplerequest: 'sample',
  sample: 'sample',
  document: 'document',
  contactcompany: 'account',
  contact: 'contact',
  campaign: 'campaign',
  tag: 'tag',
  distributor: 'distributor',
  gpo: 'gpo',
  product: 'product',
  rebate: 'rebate',
  rebateoffer: 'rebate',
  rebateofferterms: 'rebate',
  rebatesubmission: 'rebate',
}

/** Plain, render-agnostic description of an activity event. The component layer
 *  turns this into a fb-ui `ActivityItem` (icons, the email disclosure/modal). */
export interface ActivityDescriptor {
  id: number
  timestamp: string
  type: ActivityType
  /** Who did it — person name, sender (inbound email), or automation/AI label. */
  actor: string
  /** Action sentence following the actor, e.g. "sent an email to Rachel Kim". */
  summary: string
  /** Optional secondary line (subject, reminder title, sample date, …). */
  detail?: string
  /** Present for email events — the row shows the subject and opens the full
   *  message (built from the event) in a modal on click. */
  email?: { subject: string }
  /** Raw note body (rich JSON/HTML) — the component renders it as expandable
   *  content. Present only for note events with a body. */
  noteContent?: string
  /** Target sales-stage id for a deal stage-change — the component renders it as
   *  a colored StagePill after "changed sales stage to". */
  stageChangeToId?: number
  /** Document file URL — present for non-deleted document events so the row can
   *  open the file on click (matching the detail-page document card). */
  documentFile?: string
}

/** Human label for the fallback "verb + object" sentence (the raw model name
 *  reads badly, e.g. "contactcompany"). Only the awkward ones are remapped. */
const MODEL_LABEL: Record<string, string> = {
  contactcompany: 'company',
  samplerequest: 'sample request',
  emailmessage: 'email',
}

/** Read a `field_history_objects` value by field name, coerced to a string. */
function fho(event: ActivityV2, name: string): string {
  const raw = event.field_history_objects?.find(
    (f) => f.field_name === name
  )?.value
  if (raw == null) return ''
  return typeof raw === 'string' ? raw : String(raw)
}

/** The raw `field_history_objects` entry for the first matching field name —
 *  for reading extra columns (document_file, previous_value, contact_company_name). */
function fhoField(event: ActivityV2, ...names: string[]) {
  return event.field_history_objects?.find((f) => names.includes(f.field_name))
}

/** Format a sample's `date_needed` (YYYY-MM-DD) as M/D/YYYY, parsed as a local
 *  date to avoid the timezone drift the detail-page card guards against. */
function formatSampleDate(raw: string): string {
  try {
    return format(parseISO(raw), 'M/d/yyyy')
  } catch {
    return raw
  }
}

/** Mirror the detail-page card's cancellation detection: an UPDATE to `status`
 *  whose value (an array of JSON-encoded strings) resolves to 'cancelled'. */
function isSampleCancellation(event: ActivityV2): boolean {
  if (event.action_type !== 'UPDATE' || event.field_name !== 'status') {
    return false
  }
  const raw = Array.isArray(event.value) ? event.value[0] : event.value
  let parsed = raw
  if (typeof raw === 'string' && raw.startsWith('"')) {
    try {
      parsed = JSON.parse(raw)
    } catch {
      /* keep raw */
    }
  }
  return parsed === 'cancelled'
}

/** Sentence-case a snake_case field name ("close_date" → "Close date"). */
function humanizeField(name: string): string {
  const spaced = name.replace(/_/g, ' ').trim()
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : 'a field'
}

const createVerb = (action: string) =>
  action === 'DELETE' ? 'deleted' : action === 'UPDATE' ? 'updated' : 'added'

/**
 * Map one `ActivityV2` event to a render-agnostic descriptor. Covers the
 * specialized types (email, note, reminder, sample, document, deal) with
 * faithful summaries and a sensible "verb + object / changed {field}" fallback
 * for the long tail — this is a summary side-panel, not the full-fidelity
 * detail-page formatter, so per-field value resolution (StagePill, etc.) is
 * intentionally not reproduced.
 */
export function describeActivityEvent(event: ActivityV2): ActivityDescriptor {
  const model = event.content_type?.model ?? ''
  const action = event.action_type ?? ''
  const userName = event.user_name || 'Someone'
  const base = {
    id: event.id,
    timestamp: event.created_at,
  }

  const isAutomation = AUTOMATION_USER_NAMES.has(userName)
  const isAi = AI_USER_NAMES.has(userName)
  const resolvedType: ActivityType = isAutomation
    ? 'automation'
    : isAi
      ? 'ai'
      : (MODEL_TYPE[model] ??
        (action === 'DELETE'
          ? 'deleted'
          : action === 'CREATE'
            ? 'created'
            : 'updated'))

  // Email — the one type with an inbound/outbound split + a modal preview.
  if (model === 'emailmessage') {
    const outbound = fho(event, 'outbound') === 'true'
    const subject = fho(event, 'subject') || '(No subject)'
    const to = fho(event, 'to')
    return {
      ...base,
      type: resolvedType,
      actor: userName,
      summary: outbound
        ? `sent an email${to ? ` to ${to}` : ''}`
        : 'sent you an email',
      detail: subject,
      email: { subject },
    }
  }

  if (MODEL_TYPE[model] === 'note') {
    const noteContent = fho(event, 'content')
    return {
      ...base,
      type: resolvedType,
      actor: userName,
      summary: `${createVerb(action)} a note`,
      noteContent: noteContent || undefined,
    }
  }

  if (model === 'reminder') {
    const title = fho(event, 'title')
    const content = fho(event, 'content')
    const verb = action === 'CREATE' ? 'scheduled' : createVerb(action)
    return {
      ...base,
      type: resolvedType,
      actor: userName,
      // Title rides the sentence (as the detail page does); the body/content is
      // the secondary line.
      summary: `${verb} a reminder${title ? `: ${title}` : ''}`,
      detail: content || undefined,
    }
  }

  if (model === 'samplerequest' || model === 'sample') {
    const cancelled = isSampleCancellation(event)
    const requestNumber = fho(event, 'request_number')
    const dateNeeded = fho(event, 'date_needed')
    const verb = action === 'CREATE' ? 'submitted' : 'updated'
    return {
      ...base,
      type: resolvedType,
      actor: userName,
      summary: cancelled
        ? 'cancelled a sample request'
        : `${verb} a sample request${requestNumber ? ` #${requestNumber}` : ''}`,
      detail: cancelled
        ? 'Request cancelled'
        : dateNeeded
          ? `Date needed: ${formatSampleDate(dateNeeded)}`
          : undefined,
    }
  }

  if (model === 'document') {
    // Mirror the detail-page DocumentEventCard: name/tags field carries the
    // name + file url + replacement flag; company_association carries a re-link.
    const field = fhoField(event, 'name', 'tags')
    const assocField = fhoField(event, 'company_association')
    const contactCompanyName =
      action === 'CREATE'
        ? (event.field_history_objects?.[0]?.contact_company_name ?? undefined)
        : undefined
    const nameFromDoc = event.field_history_objects?.find(
      (f) => f.document_name
    )?.document_name
    const documentName = String(
      field?.value || field?.previous_value || nameFromDoc || ''
    )
    const documentFile =
      field?.document_file ??
      event.field_history_objects?.find((f) => f.document_file)
        ?.document_file ??
      undefined
    const isReplacement =
      field?.document_is_replacement ??
      event.field_history_objects?.find((f) => f.document_is_replacement)
        ?.document_is_replacement
    const isDeleted = !documentName
    const displayName = isDeleted
      ? 'deleted document'
      : action === 'DELETE'
        ? String(field?.previous_value || documentName)
        : documentName

    let summary: string
    if (action === 'CREATE') {
      const baseAction = isReplacement
        ? 'replaced a document'
        : 'uploaded a document'
      summary = contactCompanyName
        ? `${baseAction} to ${contactCompanyName}`
        : baseAction
    } else if (action === 'UPDATE') {
      const newCompany = assocField?.contact_company_name
      summary = newCompany
        ? `changed company association to ${newCompany}`
        : 'updated a document'
    } else if (action === 'DELETE') {
      summary = 'deleted a document'
    } else {
      summary = 'modified a document'
    }

    return {
      ...base,
      type: resolvedType,
      actor: userName,
      summary,
      detail: displayName || undefined,
      // The row opens the file (new tab) when there's one and it wasn't deleted.
      documentFile:
        action !== 'DELETE' && documentFile ? documentFile : undefined,
    }
  }

  if (model === 'deal' && action === 'UPDATE') {
    if (event.field_name === 'sales_stage_id') {
      // The component resolves this id → a colored StagePill (real stage name +
      // color); the string summary is the fallback if it can't be resolved.
      const stageId = Number(event.value)
      return {
        ...base,
        type: resolvedType,
        actor: userName,
        summary: 'changed sales stage',
        stageChangeToId:
          Number.isFinite(stageId) && stageId > 0 ? stageId : undefined,
      }
    }
    const fieldLabel =
      event.field_name === 'account_owner_id'
        ? 'account owner'
        : humanizeField(event.field_name || '').toLowerCase()
    return {
      ...base,
      type: resolvedType,
      actor: userName,
      summary: `changed ${fieldLabel}`,
    }
  }

  // Long tail: a value-agnostic "changed {field}" for updates, else verb+object.
  if (action === 'UPDATE' && event.field_name) {
    return {
      ...base,
      type: resolvedType,
      actor: userName,
      summary: `changed ${humanizeField(event.field_name).toLowerCase()}`,
    }
  }

  const objectLabel = MODEL_LABEL[model] ?? model ?? 'record'
  return {
    ...base,
    type: resolvedType,
    actor: userName,
    summary: `${createVerb(action)} a ${objectLabel}`,
  }
}
